// Brief overlay shown while the party is migrating between regions. No
// buttons — purely informational. Dismissed by the caller once the new
// PartyRoom is live and everyone has reconnected (or the migration
// failed and the caller is falling back).
//
// Reuses the searching-modal styles for visual consistency. Sits at the
// same z-index as SearchingModal so it stacks correctly above the
// PartyPanel.

import { t } from '../../i18n';

const STYLES_ID = 'region-switch-modal-styles';
const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Chewy&display=swap');

.region-switch-modal {
  position: fixed; inset: 0;
  display: flex; align-items: center; justify-content: center;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(8px);
  z-index: 200;
  font-family: 'Chewy', ui-sans-serif, system-ui, sans-serif;
  color: #FFFFFF;
  opacity: 0;
  transition: opacity 200ms ease;
  pointer-events: auto;
}
.region-switch-modal.shown { opacity: 1; }
.region-switch-modal.closing { opacity: 0; }
.region-switch-modal .card {
  pointer-events: auto;
  background: #1E232C;
  border-radius: 18px;
  box-shadow: 0 16px 48px rgba(0,0,0,0.5);
  padding: 32px 36px;
  display: flex; flex-direction: column; align-items: center;
  gap: 20px;
  min-width: 420px; max-width: 90vw;
  transform: scale(0.96);
  transition: transform 200ms cubic-bezier(0.2, 0.9, 0.2, 1);
}
.region-switch-modal.shown .card { transform: scale(1); }
.region-switch-modal .title-row {
  display: flex; align-items: center; gap: 16px;
  font-size: 32px;
  -webkit-text-stroke: 3px rgba(0,0,0,0.22);
  paint-order: stroke fill;
}
.region-switch-modal .spin {
  width: 26px; height: 26px;
  border: 3px solid rgba(255,255,255,0.4);
  border-top-color: #FFFFFF;
  border-radius: 50%;
  animation: region-switch-rotate 900ms linear infinite;
}
@keyframes region-switch-rotate { to { transform: rotate(360deg); } }
.region-switch-modal .footnote {
  font-size: 18px;
  color: rgba(255,255,255,0.7);
  text-align: center;
  max-width: 360px;
}
`;

export interface RegionSwitchModalHandle {
  setRegionLabel(label: string): void;
  dismiss(): void;
}

export function showRegionSwitchModal(initialRegionLabel: string, footnote?: string): RegionSwitchModalHandle {
  ensureStyles();
  const root = ensureRoot();
  const wrap = document.createElement('div');
  wrap.className = 'region-switch-modal';
  const fn = footnote ?? t('region.switchingFootnote');
  wrap.innerHTML = `
    <div class="card">
      <div class="title-row">
        <div class="spin"></div>
        <div class="title-text">${escapeHtml(t('region.switching', { region: initialRegionLabel }))}</div>
      </div>
      ${fn ? `<div class="footnote">${escapeHtml(fn)}</div>` : ''}
    </div>
  `;
  root.appendChild(wrap);
  requestAnimationFrame(() => wrap.classList.add('shown'));

  let dismissed = false;
  const titleEl = wrap.querySelector('.title-text') as HTMLElement;

  return {
    setRegionLabel(label: string): void {
      if (dismissed) return;
      titleEl.textContent = t('region.switching', { region: label });
    },
    dismiss(): void {
      if (dismissed) return;
      dismissed = true;
      wrap.classList.add('closing');
      setTimeout(() => wrap.remove(), 220);
    }
  };
}

function ensureRoot(): HTMLElement {
  let root = document.getElementById('match-modals');
  if (!root) {
    root = document.createElement('div');
    root.id = 'match-modals';
    root.style.cssText = `position:fixed; inset:0; z-index:150; pointer-events:none;`;
    document.body.appendChild(root);
  }
  return root;
}

function ensureStyles(): void {
  if (document.getElementById(STYLES_ID)) return;
  const s = document.createElement('style');
  s.id = STYLES_ID;
  s.textContent = STYLES;
  document.head.appendChild(s);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}
